/**
 * Player ship: NMS-style arcade flight. The ship always travels along its
 * nose (no drift); throttle sets a fraction of the current speed envelope,
 * which scales with altitude so planet surfaces feel fast and close passes
 * feel slow. Pulse drive covers interplanetary distances.
 *
 * Inside an ATMOSPHERE the arcade core gains an aero layer, blended by air
 * density (0 at the band edge → 1 at the surface):
 *  - GRAVITY pulls radially (ATMO_G at full density).
 *  - LIFT carries weight above STALL_SPEED — but only insofar as the wings
 *    face the sky: banking tilts the lift vector and bleeds altitude, and
 *    inverted flight carries nothing, so turns cost height like a plane's.
 *  - HOVER assist (vectored thrusters) holds slow ships up below
 *    HOVER_SPEED, keeping landing/parking/disembark flows intact; the gap
 *    between hover and stall is the mushy regime where you sink.
 *  - DRAG shaves the speed envelope in dense air, and aero damping caps
 *    the accumulated sink at a terminal rate (ATMO_G / SINK_DECAY).
 * Airless planets stay pure-arcade: no air, vectored thrusters rule.
 *
 * Position lives in system space (float64 via JS numbers); collision clamps
 * against the analytic terrain height function — the exact same function the
 * meshes are built from, so you land on what you see.
 */
import * as THREE from "three";
import { WORLD_SCALE } from "@hyperlight/shared";
import type { Input } from "../engine/input";
import type { StarSystem } from "../world/starsystem";
import { TerrainContacts } from '../world/terrainContacts';

const MOUSE_SENS = 0.0016;
const ROLL_RATE = 1.9;
const HOVER_CLEARANCE = 4;
/** Hull half-width for building collision (buildings inflate by this). */
const SHIP_HULL_R = 3;

// --- Atmospheric flight model ---
/** Surface gravity at full air density (m/s²). */
const ATMO_G = 14;
/** Wings carry the ship's full weight from this airspeed up (m/s). */
const STALL_SPEED = 60;
/** Below this airspeed the vectored thrusters hold the ship up (m/s). */
const HOVER_SPEED = 45;
/** Fraction of the speed envelope lost to dense air at the surface. */
const ATMO_DRAG = 0.3;
/** Aero damping on the sink vector → terminal sink ≈ ATMO_G / this. */
const SINK_DECAY = 0.35;

// --- Vertical thrusters (Space / X) ---
/** Climb/descend acceleration (m/s²). */
const VERT_ACCEL = 55;
/** Top climb/descend rate (m/s) — a maneuvering thruster, not a second engine. */
const VERT_MAX = 60;
/** Spool-down once the key is released (per second). */
const VERT_DECAY = 4;

export type FlightMode = "flight" | "boost" | "pulse" | "landed";

export class Ship {
  readonly pos = new THREE.Vector3();
  readonly quat = new THREE.Quaternion();
  readonly vel = new THREE.Vector3();
  /**
   * Pose at the START of the latest sim tick. The renderer interpolates
   * between (prevPos, prevQuat) and (pos, quat) by the accumulator fraction
   * so 60 Hz simulation looks smooth on any refresh rate.
   */
  readonly prevPos = new THREE.Vector3();
  readonly prevQuat = new THREE.Quaternion();
  speed = 0;
  throttle = 0.15;
  pulse = false;
  mode: FlightMode = "flight";
  /** Distance to nearest planet surface, refreshed every tick. */
  surfaceDist = Infinity;
  nearestPlanetName = "—";
  /** When set, the ship comes about to face this direction (nav heading). */
  headingDir: THREE.Vector3 | null = null;
  /** While a nav target is armed, Space charges hyperlight instead of pulse. */
  suppressPulse = false;
  /** Mouse yaw rate this tick (rad/s) — drives the cosmetic bank roll. */
  yawRate = 0;
  /** Air density at the ship (0 vacuum → 1 surface), from the last tick. */
  airDensity = 0;
  /** Installed-module stat modifiers (phase 7 upgrades). */
  boostMul = 3.5;
  // Pulse-drive top speed scales with WORLD_SCALE so crossing the (now 10×
  // larger) inter-planet distances takes about as long as it used to.
  pulseTop = 32000 * WORLD_SCALE;
  pulseResponse = 0.8;

  private fwd = new THREE.Vector3();
  private tmp = new THREE.Vector3();
  private tmp2 = new THREE.Vector3();
  private terrainContacts = new TerrainContacts();
  private previousPosition = new THREE.Vector3();
  private tmpQ = new THREE.Quaternion();
  private dir = new THREE.Vector3();
  /** Accumulated aero sink (gravity minus lift/hover), system space. */
  private sink = new THREE.Vector3();
  /** Vertical thruster axis this tick: +1 climb (Space), -1 descend (X). */
  private vertIn = 0;
  /** Current climb/descend rate (m/s, signed) — spools up and back down. */
  private vertSpeed = 0;
  /** Low-passed attitude deltas (per-tick radians) — see update() for why. */
  private smPitch = 0;
  private smYaw = 0;
  private upV = new THREE.Vector3();

  /** Snap the interpolation history (after teleports — no smearing across). */
  snapVisual(): void {
    this.prevPos.copy(this.pos);
    this.prevQuat.copy(this.quat);
    this.sink.set(0, 0, 0); // teleports don't carry falling momentum
    this.vertSpeed = 0;
  }

  // NOTE: prevPos/prevQuat are snapshotted by the CALLER before the body
  // carry runs — the render lerp must span carry + physics as one motion,
  // or entities step the frame while interpolated planets glide (jitter).
  update(dt: number, input: Input, system: StarSystem): void {
    const pending = system.nearestPlanet(this.pos);
    if (pending && pending.surfaceDist <= 9000 && pending.planet.terrainSolidsReady === false) return;
    // --- Attitude ---
    const { dx, dy } = input.consumeMouse();
    // Mouse deltas accumulate per RENDER frame but are consumed per FIXED tick:
    // in a two-tick frame the first tick eats the whole delta and the second
    // starves, so the attitude rate ALTERNATES at ~30 Hz — the render slerp
    // reproduces the alternation and a sustained bank judders (glaring on the
    // Lightspeed's 6 m wingspan; sub-pixel on the small parts ship). A short
    // low-pass redistributes the deltas into a steady rate; DC gain is 1, so
    // total rotation is preserved and the ~50 ms tail reads as hull inertia.
    const smoothK = 1 - Math.exp(-dt / 0.05);
    this.smPitch += (-dy * MOUSE_SENS * (input.invertY ? -1 : 1) - this.smPitch) * smoothK;
    this.smYaw += (-dx * MOUSE_SENS - this.smYaw) * smoothK;
    const pitch = this.smPitch;
    const yaw = this.smYaw;
    this.yawRate = yaw / dt;
    let roll = 0;
    if (input.isDown("KeyA")) roll += ROLL_RATE * dt;
    if (input.isDown("KeyD")) roll -= ROLL_RATE * dt;

    this.tmpQ.setFromEuler(new THREE.Euler(pitch, yaw, roll, "YXZ"));
    this.quat.multiply(this.tmpQ).normalize();

    // Nav heading: come about toward the selected galaxy. Cleared once
    // aligned; manual steering simply fights (and can outpace) the slew.
    if (this.headingDir) {
      const up = this.tmp.set(0, 1, 0).applyQuaternion(this.quat);
      const m = new THREE.Matrix4().lookAt(new THREE.Vector3(), this.tmp2.copy(this.headingDir), up);
      this.tmpQ.setFromRotationMatrix(m);
      this.quat.rotateTowards(this.tmpQ, 1.4 * dt);
      this.fwd.set(0, 0, -1).applyQuaternion(this.quat);
      if (this.fwd.dot(this.headingDir) > 0.9995) this.headingDir = null;
    }

    // --- Throttle / modes ---
    if (input.isDown("KeyW")) this.throttle = Math.min(1, this.throttle + dt * 0.8);
    if (input.isDown("KeyS")) this.throttle = Math.max(-0.25, this.throttle - dt * 0.8);
    if (input.wasPressed("KeyZ")) this.throttle = 0;
    // HOLD for hyperlight: pulse engages while R is down, drops on
    // release (was a toggle). The low-altitude cut in physics() wins —
    // don't re-engage under it while the key is still held.
    this.pulse = input.isDown("KeyR") && !this.suppressPulse && this.surfaceDist >= 1500;

    // Vectored thrusters: Space climbs, X descends.
    this.vertIn =
      (input.isDown("Space") ? 1 : 0) -
      (input.isDown("KeyX") ? 1 : 0);

    this.physics(dt, system, input.isDown("ShiftLeft") || input.isDown("ShiftRight"));
  }

  /**
   * Hands-off flight while a pause/overlay owns the controls: the ship keeps
   * moving but eases down to cruising throttle instead of freezing mid-air.
   */
  coast(dt: number, system: StarSystem): void {
    const pending = system.nearestPlanet(this.pos);
    if (pending && pending.surfaceDist <= 9000 && pending.planet.terrainSolidsReady === false) return;
    this.pulse = false;
    this.yawRate = 0;
    this.smPitch = 0; // a menu is not a mouse turn — no smoothed tail on resume
    this.smYaw = 0;
    this.vertIn = 0; // a menu is not a thruster input either
    // Ease the throttle to ZERO: a menu is not a cruise — a 0.25 idle
    // cruise carried players kilometers away while they read their
    // inventory ("float away"). Physics still runs (gravity, drag, hover).
    this.throttle += (0 - this.throttle) * (1 - Math.exp(-1.5 * dt));
    this.physics(dt, system, false);
  }

  private physics(dt: number, system: StarSystem, boosting: boolean): void {
    // --- Speed envelope from altitude ---
    const near = system.nearestPlanet(this.pos);
    let surfaceDist = Infinity;
    if (near) {
      this.nearestPlanetName = near.planet.def.name;
      this.dir.copy(this.pos).sub(near.planet.pose.center);
      const r = this.dir.length();
      this.dir.divideScalar(r || 1);
      const surfaceR = near.planet.surfaceRadiusWorld(this.dir);
      surfaceDist = r - surfaceR;
    }
    this.surfaceDist = surfaceDist;

    // Pulse drive auto-disengages near terrain.
    if (this.pulse && surfaceDist < 1500) this.pulse = false;

    // Air density: 0 outside the atmosphere band, 1 at the surface.
    const atmoH = near?.planet.def.atmosphere?.height ?? 0;
    const rho =
      atmoH > 0 ? Math.max(0, 1 - Math.max(0, surfaceDist) / (atmoH * 3)) : 0;
    this.airDensity = rho;

    const altFactor = Math.min(1, Math.max(0, surfaceDist) / 25000);
    let maxSpeed = 70 + altFactor * 1900;
    if (boosting) maxSpeed *= this.boostMul;
    maxSpeed *= 1 - ATMO_DRAG * rho; // dense air drags the whole envelope
    if (this.pulse) maxSpeed = this.pulseTop;

    const targetSpeed = maxSpeed * this.throttle;
    // Pulse winds up/down slower than normal flight for a supercruise feel.
    const response = this.pulse || Math.abs(this.speed) > 8000 ? this.pulseResponse : 3.5;
    this.speed += (targetSpeed - this.speed) * (1 - Math.exp(-response * dt));

    this.mode = this.pulse ? "pulse" : boosting ? "boost" : "flight";

    // --- Integrate ---
    this.fwd.set(0, 0, -1).applyQuaternion(this.quat);
    this.vel.copy(this.fwd).multiplyScalar(this.speed);

    // Approach braking: cap the CLOSING speed toward the nearest planet by a
    // distance envelope, so a head-on pulse approach decelerates smoothly
    // instead of slamming into the auto-cut. Oblique passes (low closing
    // component) and outbound burns are unaffected.
    if (near && surfaceDist < 60000 * WORLD_SCALE) {
      const closing = -this.vel.dot(this.dir); // this.dir points away from the planet
      if (closing > 0) {
        const safeClosing = 90 + surfaceDist * 0.55;
        if (closing > safeClosing) {
          const brake = 1 - Math.exp(-6 * dt);
          const scale = (closing - (closing - safeClosing) * brake) / closing;
          this.speed *= scale;
          this.vel.multiplyScalar(scale);
        }
      }
    }

    // --- ONE gravity model: contained wells around every body ---
    // Inverse-square with the tail subtracted, ending cleanly at 2.5 radii
    // — wells hug their bodies instead of leaking across the system.
    // Normalized: every body lands in a similar comfortable band. Inside
    // an atmosphere, lift and hover thrust OFFSET the well (planes fly,
    // parked ships park); in vacuum the well is naked drift.
    if (!this.pulse) {
      const well = (R: number, r: number, g0: number): number => {
        const cut = R * 2.5;
        if (r >= cut) return 0;
        return g0 * Math.max(0, (R / r) * (R / r) - (R / cut) * (R / cut));
      };
      const rs = system.def.star.radius;
      const dStar = this.pos.length() || 1;
      const gStar = well(rs, dStar, 18);
      if (gStar > 0) {
        this.sink.addScaledVector(this.tmp.copy(this.pos).divideScalar(-dStar), gStar * dt);
      }
      if (near) {
        const sizeG = Math.min(1.15, Math.max(0.7, near.planet.def.radius / 2500));
        const Rp = near.planet.def.radius;
        const dP = Math.max(surfaceDist + Rp, Rp);
        // Gentle by design: enough to feel the world, never a tractor.
        let gP = well(Rp, dP, ATMO_G * sizeG * 0.35);
        if (gP > 0) {
          const vAir = Math.abs(this.speed);
          // Wings carry weight only insofar as they face the sky — and
          // only insofar as there IS air. Banking tilts the lift vector;
          // inverted flight carries nothing.
          this.tmp2.set(0, 1, 0).applyQuaternion(this.quat);
          const lift =
            Math.min(1, (vAir / STALL_SPEED) ** 2) * Math.max(0, this.tmp2.dot(this.dir)) * rho;
          // Vectored-thruster hover is engine thrust: it holds slow flight
          // up with or without air (landing, parking, vacuum stationkeeping).
          const hover = Math.max(0, 1 - vAir / HOVER_SPEED);
          gP *= Math.max(0, 1 - lift - hover);
        }
        if (gP > 0) this.sink.addScaledVector(this.dir, -gP * dt);
      }
      // Terminal drift: gravity is a current, not a railgun.
      const sl = this.sink.length();
      if (sl > 70) this.sink.multiplyScalar(70 / sl);
    }

    // Aero damping doubles as terminal velocity and as the bleed-off that
    // clears residual sink once the ship leaves the atmosphere.
    this.sink.multiplyScalar(Math.exp(-SINK_DECAY * rho * dt - 0.05 * dt));
    this.vel.add(this.sink);

    // --- Vertical thrusters: Space climbs, X descends ---
    // "Straight up" means AWAY FROM THE PLANET, not up the hull: at a body the
    // axis is the radial, so a banked ship still gains altitude instead of
    // sliding sideways. In open space there is no down — the hull's own up is
    // the axis. Supercruise is not a hover platform: no thrusters under pulse.
    // Added to vel (not to sink) so the settlement sweep and the terrain clamp
    // below both see the climb — you cannot thrust through a roof or a floor.
    if (this.pulse) {
      this.vertSpeed = 0;
    } else {
      if (this.vertIn !== 0) this.vertSpeed += this.vertIn * VERT_ACCEL * dt;
      else this.vertSpeed *= Math.exp(-VERT_DECAY * dt);
      this.vertSpeed = Math.max(-VERT_MAX, Math.min(VERT_MAX, this.vertSpeed));
      if (Math.abs(this.vertSpeed) > 0.01) {
        if (near) this.upV.copy(this.dir); // radial, away from the planet centre
        else this.upV.set(0, 1, 0).applyQuaternion(this.quat);
        this.vel.addScaledVector(this.upV, this.vertSpeed);
      }
    }

    const preX = this.pos.x;
    const preY = this.pos.y;
    const preZ = this.pos.z;
    this.pos.addScaledVector(this.vel, dt);

    // --- Settlements are SOLID: sweep this tick's travel against the
    // grown settlement's CityGraph-derived volumes (planet-local, shared with bolts and the
    // server) — a wall stops the hull and sheds the into-wall velocity
    // (slide), a roof rests it like ground. Swept, so speed can't tunnel.
    if (near && surfaceDist < 800) {
      const pose = near.planet.pose;
      this.tmp.set(preX, preY, preZ).sub(pose.center).applyQuaternion(pose.invQuat);
      this.tmp2.copy(this.pos).sub(pose.center).applyQuaternion(pose.invQuat);
      const hit = near.planet.settlementColliders?.segmentCast(
        this.tmp.x, this.tmp.y, this.tmp.z,
        this.tmp2.x, this.tmp2.y, this.tmp2.z,
        SHIP_HULL_R,
      );
      this.previousPosition.set(preX, preY, preZ);
      const cliffT = this.terrainContacts.sweep(near.planet, this.previousPosition, this.pos, SHIP_HULL_R);
      const cliffFirst = cliffT !== null && (!hit || cliffT < hit.t);
      if (hit || cliffT !== null) {
        if (cliffFirst) this.tmp.copy(this.terrainContacts.normal);
        else this.tmp.set(...hit!.n).applyQuaternion(pose.quat);
        const t = cliffFirst ? cliffT! : hit!.t;
        const separation = cliffFirst ? Math.max(.25, this.terrainContacts.hit.penetration + .005) : .25;
        this.pos.set(preX, preY, preZ).addScaledVector(this.vel, dt * t).addScaledVector(this.tmp, separation);
        const vn = this.vel.dot(this.tmp);
        if (vn < 0) this.vel.addScaledVector(this.tmp, -vn);
        const sn = this.sink.dot(this.tmp);
        if (sn < 0) this.sink.addScaledVector(this.tmp, -sn);
      }
    }

    // --- Terrain: a ground-effect CUSHION, then the hard floor ---
    // The closing rate bleeds away progressively through the last ~40 m,
    // so skimming terrain feels like riding a pillow of thrust instead of
    // bouncing off an invisible wall; the hard clamp underneath almost
    // never has anything left to absorb.
    if (near && surfaceDist < 4000) {
      this.tmp.copy(this.pos).sub(near.planet.pose.center);
      const r = this.tmp.length();
      this.dir.copy(this.tmp).divideScalar(r || 1);
      let minR = near.planet.surfaceRadiusWorld(this.dir) + HOVER_CLEARANCE;
      if (near.planet.def.seaRadius !== null) {
        minR = Math.max(minR, near.planet.def.seaRadius + HOVER_CLEARANCE);
      }
      const cushion = 40;
      if (r < minR + cushion) {
        const k = 1 - Math.max(0, r - minR) / cushion; // 0 at edge → 1 at floor
        const vn = this.vel.dot(this.dir);
        if (vn < 0) this.vel.addScaledVector(this.dir, -vn * k * k);
      }
      if (r < minR) {
        this.pos.copy(near.planet.pose.center).addScaledVector(this.dir, minR);
        const vn = this.vel.dot(this.dir);
        if (vn < 0) this.vel.addScaledVector(this.dir, -vn);
        // The ground also absorbs accumulated sink — no stored falling
        // momentum while resting on the clamp. Same for a descent thruster
        // held into the deck: it must not have to unwind before you lift off.
        const sn = this.sink.dot(this.dir);
        if (sn < 0) this.sink.addScaledVector(this.dir, -sn);
        if (this.vertSpeed < 0) this.vertSpeed = 0;
        this.surfaceDist = HOVER_CLEARANCE;
      }
    }

    // --- The star has a SURFACE now, and you cannot land on it ---
    {
      const minStar = system.def.star.radius * 1.04;
      const rStar = this.pos.length() || 1;
      if (rStar < minStar + 1500) {
        this.tmp.copy(this.pos).divideScalar(rStar); // outward
        const k = 1 - Math.max(0, rStar - minStar) / 1500;
        const vn = this.vel.dot(this.tmp);
        if (vn < 0) this.vel.addScaledVector(this.tmp, -vn * k * k);
        if (rStar < minStar) {
          this.pos.copy(this.tmp).multiplyScalar(minStar);
          const vn2 = this.vel.dot(this.tmp);
          if (vn2 < 0) this.vel.addScaledVector(this.tmp, -vn2);
          const sn2 = this.sink.dot(this.tmp);
          if (sn2 < 0) this.sink.addScaledVector(this.tmp, -sn2);
          this.pulse = false;
        }
      }
    }

    // Parked: low and slow IS landed — hovering at clearance with the
    // throttle cut must count even when the clamp isn't actively engaged
    // (nose level or up), or the disembark prompt never appears.
    if (
      this.surfaceDist <= HOVER_CLEARANCE + 1.5 &&
      Math.abs(this.speed) < 2.5 &&
      Math.abs(this.throttle) < 0.05
    ) {
      this.mode = "landed";
    }
  }

  get altitude(): number {
    return this.surfaceDist;
  }
}
